import { useEffect, useRef } from 'react';

/**
 * useReveal — attaches an IntersectionObserver to the returned ref.
 * When the element enters view it gains the `.is-in` class (see
 * .reveal styles in globals.css). Observes once then detaches.
 *
 * Usage:
 *   const ref = useReveal();
 *   return <div ref={ref} className="reveal">…</div>;
 */
export default function useReveal(options = { rootMargin: '0px 0px -60px 0px', threshold: 0.08 }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      el.classList.add('is-in');
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      options
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return ref;
}
